<template>
  <main class="flex-1 relative overflow-y-auto focus:outline-none" tabindex="0">
    <div id="repairContainer">
      <div class="my-6 max-w-1/6 mx-auto px-4 sm:px-6 lg:px-8 ">
        <div class="md:flex md:items-center md:justify-between">
          <div class="flex min-w-0">
            <svg-vue class="content-title-icon" icon="font-awesome.clipboard-list-solid"></svg-vue>
            <h1 class="content-title">
              {{ $t('Repair report') }}
            </h1>
          </div>
          <p v-if="generatedAt && report.list.length">{{ $t('Generated at ') }}: {{ generatedAt }}</p>
          <div class="flex md:mt-0 md:ml-4">
            <button @click="printReport" class="btn btn-secondary">
              <svg-vue class="h-5 w-5" icon="font-awesome.print-solid"></svg-vue>
            </button>
          </div>
        </div>
      </div>
      <div class="my-6 max-w-1/6 mx-auto px-4 sm:px-6 md:px-8">
        <template>
          <div class="mb-1">
            <div class="rounded-md md:flex ">
              <div class="relative inline-flex rounded-none">
                <select @change="letsUpdateReport" aria-label="Sort by" class="form-input bg-white" id="duration" v-model="filters.duration">
                  <option :value="null">{{ $t('By duration') }}</option>
                  <option value="day">{{ $t('Daily') }}</option>
                  <option value="month">{{ $t('Monthly') }}</option>
                  <option value="year">{{ $t('Yearly') }}</option>
                </select>
              </div>
              <div class="relative inline-flex rounded-none">
                <select
                  @change="letsUpdateReport"
                  aria-label="Sort by"
                  class="form-input bg-white"
                  id="is_device_collected"
                  v-model="filters.is_device_collected"
                >
                  <option :value="null">{{ $t('By device') }}</option>
                  <option :value="1">{{ $t('collected') }}</option>
                  <option :value="0">{{ $t('waiting') }}</option>
                </select>
              </div>
              <div class="relative inline-flex rounded-none">
                <select @change="letsUpdateReport" aria-label="Sort by" class="form-input bg-white" id="is_manual" v-model="filters.is_manual">
                  <option :value="null">{{ $t('By type') }}</option>
                  <option :value="0">{{ $t('standard') }}</option>
                  <option :value="1">{{ $t('manual') }}</option>
                </select>
              </div>
              <div class="relative inline-flex rounded-none">
                <select @change="letsUpdateReport" aria-label="Sort by" class="form-input bg-white" id="payment" v-model="filters.payment">
                  <option :value="null">{{ $t('By payment') }}</option>
                  <option :value="1">{{ $t('paid') }}</option>
                  <option :value="0">{{ $t('unpaid') }}</option>
                </select>
              </div>
            </div>
            <div class="rounded-md shadow-sm md:flex">
              <div class="relative inline-flex rounded-none">
                <select @change="letsUpdateReport" aria-label="Sort by" class="form-input bg-white" id="status" v-model="filters.status">
                  <option :value="null">{{ $t('By status') }}</option>
                  <template v-for="(status, index) in statusList">
                    <option :key="index" :value="status.id">
                      {{ status.body }}
                    </option>
                  </template>
                </select>
              </div>
              <div class="relative inline-flex rounded-none">
                <select @change="letsUpdateReport" aria-label="Sort by" class="form-input bg-white" id="status" v-model="filters.priority">
                  <option :value="null">{{ $t('By priority') }}</option>
                  <template v-for="(priority, index) in priorityList">
                    <option :key="index" :value="priority.id">
                      {{ priority.name }}
                    </option>
                  </template>
                </select>
              </div>
              <div class="relative inline-flex rounded-none">
                <select @change="letsUpdateReport" aria-label="Sort by" class="form-input bg-white" id="status" v-model="filters.technician">
                  <option :value="null">{{ $t('By technician') }}</option>
                  <template v-for="(technician, index) in technicianList">
                    <option :key="index" :value="technician.id">
                      {{ technician.name }}
                    </option>
                  </template>
                </select>
              </div>
            </div>
            <div class="md:flex">
              <input id="start" @change="letsUpdateReport" type="date" v-model="filters.startdate" :title="$t('Date start')" class="form-input" />
              <input id="end" @change="letsUpdateReport" type="date" v-model="filters.enddate" :title="$t('Date end')" class="form-input" />
            </div>
          </div>
          <div class="pb-2">
            <div class="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
              <div class="bg-white overflow-hidden shadow rounded-lg">
                <loading :status="loading"></loading>
                <div class="px-4 py-5 sm:p-6">
                  <div class="text-sm font-medium truncate">
                    {{ $t('Total Amount') }}
                  </div>
                  <div class="mt-1 text-3xl font-semibold text-gray-900">
                    {{ report.amount | currency }}
                  </div>
                </div>
              </div>
              <div class="bg-white overflow-hidden shadow rounded-lg">
                <loading :status="loading"></loading>
                <div class="px-4 py-5 sm:p-6">
                  <div class="text-sm font-medium truncate">{{ $t('Total Cost') }}</div>
                  <div class="mt-1 text-3xl font-semibold text-gray-900">
                    {{ report.cost | currency }}
                  </div>
                </div>
              </div>
              <div class="bg-white overflow-hidden shadow rounded-lg">
                <loading :status="loading"></loading>
                <div class="px-4 py-5 sm:p-6">
                  <div class="text-sm font-medium truncate">{{ $t('Total Tax') }}</div>
                  <div class="mt-1 text-3xl font-semibold text-gray-900">
                    {{ report.tax | currency }}
                  </div>
                </div>
              </div>
              <div class="bg-white overflow-hidden shadow rounded-lg">
                <loading :status="loading"></loading>
                <div class="px-4 py-5 sm:p-6">
                  <div class="text-sm font-medium truncate">
                    {{ $t('Total Profit') }}
                  </div>
                  <div class="mt-1 text-3xl font-semibold text-gray-900">
                    {{ report.profit | currency }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="grid grid-cols-1 gap-6">
            <div class="content-card-body">
              <div class="p-4">
                <loading :status="loading"></loading>
                <template v-if="report.list.length">
                  <table class="min-w-full border-collapse block md:table">
                    <thead class="block md:table-header-group">
                      <tr
                        class="border border-grey-500 md:border-none block md:table-row absolute -top-full md:top-auto -left-full md:left-auto  md:relative "
                      >
                        <th scope="col" class="py-1 font-semibold text-left text-gray-900 uppercase">
                          {{ $t('Tracking ID') }}
                        </th>
                        <th scope="col" class="py-1 font-semibold text-left text-gray-900 uppercase">
                          {{ $t('Customer') }}
                        </th>
                        <th scope="col" class="py-1 font-semibold text-left text-gray-900 uppercase">
                          {{ $t('Cost') }}
                        </th>
                        <th scope="col" class="py-1 font-semibold text-left text-gray-900 uppercase">
                          {{ $t('Price') }}
                        </th>
                        <th scope="col" class="py-1 font-semibold text-left text-gray-900 uppercase">
                          {{ $t('Tax') }}
                        </th>
                        <th scope="col" class="py-1 font-semibold text-left text-gray-900 uppercase">
                          {{ $t('Additional') }}
                        </th>
                        <th scope="col" class="py-1 font-semibold text-left text-gray-900 uppercase">
                          {{ $t('Due') }}
                        </th>
                        <th scope="col" class="py-1 font-semibold text-left text-gray-900 uppercase">
                          {{ $t('Total') }}
                        </th>
                        <th scope="col" class="py-1 font-semibold text-left text-gray-900 uppercase">
                          {{ $t('Priority') }}
                        </th>
                        <th scope="col" class="py-1 font-semibold text-left text-gray-900 uppercase">
                          {{ $t('Status') }}
                        </th>
                        <th scope="col" class="py-1 font-semibold text-left text-gray-900 uppercase">
                          {{ $t('Collection') }}
                        </th>
                        <th scope="col" class="py-1 font-semibold text-left text-gray-900 uppercase">
                          {{ $t('Payment') }}
                        </th>
                      </tr>
                    </thead>
                    <tbody class=" bg-white divide-y divide-gray-400">
                      <tr v-for="(order, index) in report.list" :key="index">
                        <td class="px-0 w-3">
                          <barcode height="15" font-size="10" :value="order.tracking">
                            {{ order.tracking }}
                          </barcode>
                        </td>
                        <td class="p-1">
                          {{ order.name | capitalize }}
                        </td>
                        <td class="p-1">
                          {{ order.cost | currency }}
                        </td>
                        <td class="p-1">
                          {{ order.sub_total | currency }}
                        </td>
                        <td class="p-1">
                          {{ order.tax | currency }}
                        </td>
                        <td class="p-1">
                          {{ order.additional_amount | currency }}
                        </td>
                        <td class="p-1">
                          {{ order.due | currency }}
                        </td>
                        <td class="p-1">
                          {{ order.total_charges | currency }}
                        </td>
                        <td class="p-1">
                          {{ order.priority.name | capitalize }}
                        </td>
                        <td class="p-1">
                          {{ order.status.body | capitalize }}
                        </td>
                        <td class="p-1">
                          <template v-if="order.is_device_collected">
                            <span class="bg-blue-100 px-1">{{ $t('Collected') | uppercase }}</span>
                          </template>
                          <template v-else>
                            <span class="bg-yellow-100 px-1">{{ $t('waiting') | uppercase }}</span>
                          </template>
                        </td>
                        <td class="p-1">
                          <template v-if="order.payment_status">
                            <span class="bg-green-100 px-1">{{ $t('Paid') | uppercase }}</span>
                          </template>
                          <template v-else>
                            <span class="bg-red-100 px-1">{{ $t('Unpaid') | uppercase }}</span>
                          </template>
                        </td>
                      </tr>
                      <tr>
                        <th colspan="11" class="text-right">{{ $t('Total orders') }} :</th>
                        <td>{{ report.orders }}</td>
                      </tr>
                    </tbody>
                  </table>
                </template>
                <template v-else>
                  <div class="h-full flex">
                    <div class="m-auto">
                      <div class="grid grid-cols-1 justify-items-center h-full w-full px-4 py-10">
                        <div class="flex justify-center items-center">
                          <svg-vue class="h-full h-auto w-64 mb-12" icon="undraw.browsing"></svg-vue>
                        </div>
                        <div class="flex justify-center items-center">
                          <div class="w-full font-semibold text-2xl">
                            {{ $t('No records found') }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </main>
</template>
<script>
import VueBarcode from 'vue-barcode';
export default {
  name: 'report',
  metaInfo() {
    return {
      title: this.$i18n.t('Admin report'),
    };
  },
  components: {
    barcode: VueBarcode,
  },
  data() {
    return {
      loading: false,
      generatedAt: null,
      statusList: [],
      priorityList: [],
      technicianList: [],
      filters: {
        status: null,
        priority: null,
        technician: null,
        payment: null,
        duration: null,
        startdate: null,
        enddate: null,
        is_device_collected: null,
        is_manual: null,
      },
      report: {
        list: [],
        amount: 0,
        brands: 0,
        cost: 0,
        defects: 0,
        devices: 0,
        orders: 0,
        profit: 0,
        tax: 0,
      },
    };
  },
  mounted() {
    this.getFilters();
    this.generateReport();
  },
  computed: {
    anyFilter() {
      return (
        this.filters.is_manual !== null ||
        this.filters.is_device_collected !== null ||
        this.filters.startdate !== null ||
        this.filters.enddate !== null ||
        this.filters.duration !== null ||
        this.filters.status !== null ||
        this.filters.payment !== null ||
        this.filters.priority !== null
      );
    },
  },
  methods: {
    currentDateTime() {
      const current = new Date();
      const date = current.getFullYear() + '-' + (current.getMonth() + 1) + '-' + current.getDate();
      const time = current.getHours() + ':' + current.getMinutes() + ':' + current.getSeconds();
      const dateTime = date + ' ' + time;
      this.generatedAt = dateTime;
    },
    async printReport() {
      this.loading = true;
      await this.$htmlToPaper('repairContainer');
      setTimeout(() => {
        this.loading = false;
      }, 1000);
    },
    letsUpdateReport() {
      this.generateReport();
    },
    getFilters() {
      axios.get(route('repair-orders.filters')).then((response) => {
        this.statusList = response.data.statuses;
        this.priorityList = response.data.priorities;
        this.technicianList = response.data.technicians;
      });
    },
    generateReport() {
      this.loading = true;
      axios
        .get(route('repair.report'), {
          params: this.filters,
        })
        .then((response) => {
          this.loading = false;
          this.report = response.data;
          this.currentDateTime();
        })
        .catch(() => {
          this.loading = false;
        });
    },
  },
};
</script>
